class MelideOrderTracking extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-tracking-form]');
    this.input = this.querySelector('[data-tracking-input]');
    this.error = this.querySelector('[data-tracking-error]');
    this.orderError = this.querySelector('[data-tracking-error-order]');
    this.shippedError = this.querySelector('[data-tracking-error-unshipped]');
    this.result = this.querySelector('[data-tracking-result]');
    this.toolbar = this.querySelector('[data-tracking-toolbar]');
    this.stage = this.querySelector('[data-tracking-stage]');
    this.frame = this.querySelector('[data-tracking-frame]');
    this.fallback = this.querySelector('[data-tracking-fallback]');
    this.codeLabel = this.querySelector('[data-tracking-code]');
    this.externalLinks = this.querySelectorAll('[data-tracking-external]');
    this.orderButtons = this.querySelectorAll('[data-track-order]');
    this.fullscreenButton = this.querySelector('[data-tracking-fullscreen]');
    this.fullscreenLabel = this.querySelector('[data-tracking-fullscreen-label]');
    this.shippyBase = this.getAttribute('data-shippypro-base') || 'https://www.shippypro.com/tracking.html';
    this.labels = {
      enter: this.getAttribute('data-label-fullscreen') || '',
      exit: this.getAttribute('data-label-exit-fullscreen') || ''
    };
    this.knownOrders = this.loadKnownOrders();
    this.resolvingOrder = false;

    if (this.form) {
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    this.orderButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const code = button.getAttribute('data-tracking-number') || button.getAttribute('data-order-name') || '';
        const url = button.getAttribute('data-tracking-url') || '';
        this.track(code || button.getAttribute('data-order-name'), url);
      });
    });

    if (this.fullscreenButton) {
      this.fullscreenButton.addEventListener('click', this.toggleFullscreen.bind(this));
    }

    if (this.frame) {
      this.frame.addEventListener('load', () => this.syncStageSize());
    }

    document.addEventListener('fullscreenchange', this.syncFullscreenUi.bind(this));
    document.addEventListener('webkitfullscreenchange', this.syncFullscreenUi.bind(this));
    window.addEventListener('resize', this.syncStageSize.bind(this), { passive: true });
    window.visualViewport?.addEventListener('resize', this.syncStageSize.bind(this), { passive: true });
    window.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.classList.contains('is-expanded')) {
        this.setExpanded(false);
      }
    });

    const params = new URLSearchParams(window.location.search);
    const presetCode = params.get('code') || params.get('tracking') || params.get('order') || '';
    const presetUrl = params.get('url') || '';

    if (presetCode) {
      if (this.input) this.input.value = presetCode;
      this.track(presetCode, presetUrl);
    } else if (presetUrl) {
      this.track('', presetUrl);
    }
  }

  loadKnownOrders() {
    const orders = [];
    const seen = new Set();

    const add = (name, number, url) => {
      const key = this.normalizeOrderName(name);
      if (!key || seen.has(key)) return;
      seen.add(key);
      orders.push({
        name: key,
        number: String(number || '').trim(),
        url: String(url || '').trim()
      });
    };

    const jsonNode = this.querySelector('[data-tracking-orders]');
    if (jsonNode) {
      try {
        const parsed = JSON.parse(jsonNode.textContent || '[]');
        if (Array.isArray(parsed)) {
          parsed.forEach((item) => add(item.name, item.number, item.url));
        }
      } catch (error) {
        // Ignore malformed payload and keep button data.
      }
    }

    this.querySelectorAll('[data-track-order]').forEach((button) => {
      add(
        button.getAttribute('data-order-name'),
        button.getAttribute('data-tracking-number'),
        button.getAttribute('data-tracking-url')
      );
    });

    return orders;
  }

  sanitize(value) {
    return String(value || '').trim().replace(/^#+/, '').trim();
  }

  normalizeOrderName(value) {
    return this.sanitize(value).replace(/\s+/g, '').toUpperCase();
  }

  looksLikeOrder(value) {
    return /^m\d+$/i.test(this.normalizeOrderName(value));
  }

  looksLikeUrl(value) {
    return /^https?:\/\//i.test(String(value || '').trim());
  }

  digits(value) {
    return String(value || '').replace(/\D/g, '');
  }

  findKnownOrder(value) {
    const key = this.normalizeOrderName(value);
    return this.knownOrders.find((order) => order.name === key);
  }

  parseUrlParts(value) {
    const parts = { tracking: '', chiSono: '', nspediz: '', rif: '' };
    const raw = String(value || '').trim();
    if (!raw) return parts;

    try {
      const url = new URL(raw, window.location.origin);
      parts.tracking = url.searchParams.get('tracking') || '';
      parts.chiSono = url.searchParams.get('ChiSono') || '';
      parts.nspediz =
        url.searchParams.get('nspediz') ||
        url.searchParams.get('Nspediz') ||
        url.searchParams.get('nSpediz') ||
        url.searchParams.get('NSpediz') ||
        '';
      parts.rif = url.searchParams.get('RifNumCli') || '';
    } catch (error) {
      return parts;
    }

    return parts;
  }

  mergeParts(primary, secondary) {
    return {
      tracking: primary.tracking || secondary.tracking,
      chiSono: primary.chiSono || secondary.chiSono,
      nspediz: primary.nspediz || secondary.nspediz,
      rif: primary.rif || secondary.rif
    };
  }

  // Shopify stores BRT "N. spedizione" (12 digits). That is NOT ChiSono.
  // ShippyPro parcel barcode is 15–18 digits; BRT ChiSono is the first 15.
  // ShippyPro accepts the 12-digit shipment number and then links to ChiSono.
  brtShipmentUrl(code) {
    const digits = this.digits(code);
    const nspediz = digits.length === 14 ? digits.slice(2) : digits;
    return `https://vas.brt.it/vas/sped_det_show.hsm?nspediz=${encodeURIComponent(nspediz)}`;
  }

  brtParcelUrl(code) {
    const chiSono = this.digits(code).slice(0, 15);
    return `https://vas.brt.it/vas/sped_det_show.hsm?referer=sped_numspe_par.htm&ChiSono=${encodeURIComponent(chiSono)}&ClienteMittente=&DataInizio=&DataFine=&RicercaChiSono=Ricerca`;
  }

  buildUrls(value, trackingUrl) {
    const pastedUrl = this.looksLikeUrl(value) ? value : '';
    const fromShopifyUrl = this.parseUrlParts(trackingUrl);
    const fromInputUrl = this.parseUrlParts(pastedUrl);
    const fromUrl = this.mergeParts(fromShopifyUrl, fromInputUrl);
    const typed = this.sanitize(pastedUrl ? '' : value);
    const typedDigits = this.digits(typed);
    const parcelDigits = this.digits(fromUrl.tracking || fromUrl.chiSono);
    const shipmentDigits = this.digits(fromUrl.nspediz);

    let shippyQuery = '';
    if (parcelDigits.length >= 12) {
      shippyQuery = parcelDigits;
    } else if (typedDigits.length >= 12) {
      shippyQuery = typedDigits;
    } else if (shipmentDigits) {
      shippyQuery = shipmentDigits;
    } else {
      shippyQuery = typed;
    }

    const shippy = `${this.shippyBase}?tracking=${encodeURIComponent(shippyQuery)}`;
    const official =
      trackingUrl && /shippypro\.com/i.test(trackingUrl)
        ? trackingUrl
        : shippy;

    let brt;
    if (this.digits(fromUrl.chiSono).length >= 15) {
      brt = this.brtParcelUrl(fromUrl.chiSono);
    } else if (parcelDigits.length >= 15) {
      brt = this.brtParcelUrl(parcelDigits);
    } else if (typedDigits.length >= 15) {
      brt = this.brtParcelUrl(typedDigits);
    } else if (shipmentDigits.length >= 12) {
      brt = this.brtShipmentUrl(shipmentDigits);
    } else if (typedDigits.length === 12 || typedDigits.length === 14) {
      brt = this.brtShipmentUrl(typedDigits);
    } else {
      brt = this.brtShipmentUrl(shippyQuery);
    }

    return {
      shippy,
      brt,
      official,
      clean: shippyQuery || typed
    };
  }

  onSubmit(event) {
    event.preventDefault();
    if (!this.input) return;
    this.track(this.input.value);
  }

  clearErrors() {
    if (this.error) this.error.hidden = true;
    if (this.orderError) this.orderError.hidden = true;
    if (this.shippedError) this.shippedError.hidden = true;
  }

  showError(kind) {
    this.clearErrors();
    if (this.result) this.result.hidden = true;
    if (kind === 'order' && this.orderError) {
      this.orderError.hidden = false;
      return;
    }
    if (kind === 'unshipped' && this.shippedError) {
      this.shippedError.hidden = false;
      return;
    }
    if (this.error) this.error.hidden = false;
  }

  setFrameSrc(frame, url) {
    if (!frame || !url) return;
    if (frame.getAttribute('src') === url) return;
    frame.src = url;
  }

  headerOffset() {
    const styles = getComputedStyle(document.documentElement);
    const header = parseFloat(styles.getPropertyValue('--header-height')) || 0;
    const announcement = parseFloat(styles.getPropertyValue('--announcement-bars-before-header-heights')) || 0;
    return header + announcement;
  }

  isImmersive() {
    return Boolean(
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      this.classList.contains('is-expanded')
    );
  }

  fitFrame() {
    if (!this.frame || !this.stage || this.result?.hidden) return;

    const stageWidth = this.stage.clientWidth || window.innerWidth;
    const compact = stageWidth < 750;

    this.frame.style.transform = 'none';
    this.frame.style.transformOrigin = '0 0';

    if (compact) {
      this.stage.style.overflow = 'auto';
      this.frame.style.width = '920px';
      this.frame.style.minWidth = '920px';
      this.frame.style.height = '100%';
      this.stage.scrollLeft = 260;
      return;
    }

    this.stage.style.overflow = 'hidden';
    this.frame.style.width = '100%';
    this.frame.style.minWidth = '0';
    this.frame.style.height = '100%';
    this.stage.scrollLeft = 0;
  }

  syncStageSize() {
    if (!this.stage || !this.result || this.result.hidden) return;

    if (this.isImmersive()) {
      this.stage.style.height = '';
      this.fitFrame();
      return;
    }

    const viewport = window.visualViewport?.height || window.innerHeight;
    const toolbar = this.toolbar?.getBoundingClientRect().height || 0;
    const height = Math.max(Math.round(viewport - this.headerOffset() - toolbar), 360);
    this.stage.style.height = `${height}px`;
    this.fitFrame();
  }

  syncFullscreenUi() {
    const immersive = this.isImmersive();
    this.classList.toggle('is-expanded', immersive && !document.fullscreenElement && !document.webkitFullscreenElement);

    if (this.fullscreenButton) {
      this.fullscreenButton.setAttribute('aria-pressed', immersive ? 'true' : 'false');
      this.fullscreenButton.setAttribute('aria-label', immersive ? this.labels.exit : this.labels.enter);
    }

    this.querySelectorAll('[data-tracking-fs-icon]').forEach((icon) => {
      const mode = icon.getAttribute('data-tracking-fs-icon');
      icon.hidden = immersive ? mode !== 'exit' : mode !== 'enter';
    });

    if (this.fullscreenLabel) {
      this.fullscreenLabel.textContent = immersive ? this.labels.exit : this.labels.enter;
    }

    document.documentElement.classList.toggle('order-tracking-expanded', immersive);
    this.syncStageSize();
  }

  setExpanded(on) {
    this.classList.toggle('is-expanded', on);
    this.syncFullscreenUi();
  }

  toggleFullscreen() {
    const target = this.result;
    if (!target) return;

    const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement;
    if (fullscreenElement) {
      const exit = document.exitFullscreen || document.webkitExitFullscreen;
      if (exit) exit.call(document);
      return;
    }

    if (this.classList.contains('is-expanded')) {
      this.setExpanded(false);
      return;
    }

    const request = target.requestFullscreen || target.webkitRequestFullscreen;
    if (request) {
      Promise.resolve(request.call(target)).catch(() => this.setExpanded(true));
      return;
    }

    this.setExpanded(true);
  }

  track(value, overrideUrl) {
    const raw = String(value || '').trim();
    const typed = this.sanitize(raw);

    if (this.looksLikeOrder(typed) && !this.resolvingOrder) {
      const known = this.findKnownOrder(typed);
      const number = known?.number || '';
      const url = overrideUrl || known?.url || '';
      if (number || url) {
        this.resolvingOrder = true;
        if (this.input) this.input.value = typed;
        this.track(number || typed, url);
        this.resolvingOrder = false;
        return;
      }
      if (known) {
        this.showError('unshipped');
        return;
      }
      this.showError('order');
      return;
    }

    if (!this.looksLikeUrl(raw) && !overrideUrl && typed.length < 4 && this.digits(typed).length < 12) {
      this.showError('generic');
      return;
    }

    const urls = this.buildUrls(raw, overrideUrl);
    if (!urls.clean) {
      this.showError('generic');
      return;
    }

    this.clearErrors();

    if (this.input && !this.input.value) {
      this.input.value = urls.clean;
    }

    if (this.result) this.result.hidden = false;
    if (this.codeLabel) this.codeLabel.textContent = urls.clean;

    this.externalLinks.forEach((link) => {
      const type = link.getAttribute('data-tracking-external');
      if (type === 'shippypro' || type === 'official') {
        link.href = urls.official;
        link.hidden = false;
      } else if (type === 'brt' || type === 'vas') {
        link.href = urls.brt;
      }
    });

    this.setFrameSrc(this.frame, urls.brt);
    if (this.fallback) this.fallback.hidden = false;

    requestAnimationFrame(() => {
      this.syncStageSize();
      this.result?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      requestAnimationFrame(() => this.syncStageSize());
    });
  }
}

if (!customElements.get('melide-order-tracking')) {
  customElements.define('melide-order-tracking', MelideOrderTracking);
}
